import React from 'react';
import classnames from 'classnames';
import useSWR from 'swr';
import * as Sentry from '@sentry/browser';

import {
  Popper,
  InputBase,
  PopperProps,
  useMediaQuery,
  CircularProgress,
  PopperPlacementType
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { Icon, Translate, GenerationErrorDialog, TrialLimitDialog } from '@hydra/common/components';

import redirectToUrl from '@hydra/utils/redirectToUrl';
import { isValidReviewUrl } from '@hydra/utils/urlUtil';
import { createReview } from '@hydra/utils/reviewApi';
import { getOverviewData, getReviewCount } from '@hydra/utils/overviewApi';
import { sendEvent } from '@hydra/utils/gtm';
import { isLite } from '@hydra/utils/userUtil';

import { useRecaptchaV3, useI18n } from '@hydra/common/customHooks';
import { useUserContext } from '@hydra/common/context';

const EMPTY_OPTION = Symbol('empty list helper text');
const WIDTH = 400;

async function doFetch (search: string) {
  return getOverviewData(search ? { search } : { search: '' });
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    backgroundColor: theme.palette.background.secondary,
    borderRadius: theme.shape.borderRadius * 12,
    height: theme.spacing(5),
    [theme.breakpoints.up('sm')]: {
      width: WIDTH,
      minWidth: WIDTH
    }
  },
  focused: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: WIDTH // Avoid exceeding width when focused on desktop
    }
  },
  input: {
    backgroundColor: theme.palette.background.secondary,
    color: `${theme.palette.grey[900]} !important`,
    width: '100%',
    fontSize: theme.typography.fontSizeSmall,
    fontWeight: theme.typography.fontWeightSemibold,
    border: 'none',
    padding: theme.spacing(0.75, 2),
    borderRadius: theme.shape.borderRadius * 12
  },
  inputFocused: {
    borderRadius: `${theme.shape.borderRadius * 12}px ${theme.shape.borderRadius * 12}px 0 0`
  },
  favicon: {
    marginRight: theme.spacing(1),
    width: 16,
    height: 16
  }
}));

function createReviewUrl (url: string, lang: string) {
  return `/${lang}/review/${encodeURIComponent(url)}`;
}

function createProjectUrl (url: string, lang: string) {
  return `/${lang}/dashboard/${encodeURIComponent(url)}`;
}

interface PopperStyleProps {
  style: {
    width: string | number
  },
  placement: PopperPlacementType | undefined
}

const WidePopper = function (props: PopperProps) {
  const theme = useTheme();
  const displayXs = useMediaQuery(theme.breakpoints.down('xs'));
  const styleProps: PopperStyleProps = { style: { width: displayXs ? '100%' : WIDTH }, placement: 'bottom' };
  const popperProps: PopperProps = {
    ...props,
    ...styleProps
  };
  return (<Popper {...popperProps} />);
};

interface Review {
  createdAt: string;
  lastGeneratedAt: string;
  score: number;
  url: string
}

interface Project extends Review {
  crawlUsedCredits: number;
  keywordCount: number;
  subFolder: string | null;
}

interface ProjectReviewData {
  count: number | null;
  countTruncated: boolean | null;
  cursor: string | null;
}

interface ProjectData extends ProjectReviewData {
  items: Project[];
}

interface ReviewData extends ProjectReviewData {
  items: Review[];
}

interface UseLatestInterface {
  projects: ProjectData
  reviews: ReviewData
}

function useLatest (value: UseLatestInterface) {
  const latest = React.useRef(value);
  if (value !== undefined) {
    latest.current = value;
  }
  return latest.current;
}

function useDebounced (value: string, ms: number) {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  React.useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), ms);
    return () => clearTimeout(timeout);
  }, [value, ms]);
  return debouncedValue;
}

interface MagicBarInterface {
  focused: boolean;
  onBlur: () => void;
  onFocus: () => void;
  language: string;
}

export default function MagicBar ({ focused, onBlur, onFocus, language }: MagicBarInterface) {
  const classes = useStyles();
  const [loading, setLoading] = React.useState(true);
  const [input, setInput] = React.useState('');
  const debouncedInput = useDebounced(input, 250);
  const [generationError, setGenerationError] = React.useState<any>(null);
  const { translate } = useI18n();
  const skipNextBlur = React.useRef(false);
  const [disabled, setDisabled] = React.useState(true);
  const { verifyCaptcha } = useRecaptchaV3('magic-bar');

  const { user } = useUserContext();
  const isLiteUser = isLite(user);

  let maxReviews: null | number = null;
  if (user && isLiteUser && typeof user.maxReports === 'number') {
    maxReviews = !(user.maxReports === 0 || user.maxReports === -1) ? user.maxReports : null;
  }

  React.useEffect(() => {
    // enable after the component is mounted
    setDisabled(false);
  }, []);

  const { data, isValidating } = useSWR(
    focused ? [debouncedInput] : null,
    doFetch,
    { dedupingInterval: 60 * 60 * 1000 }
  );

  const latestData = useLatest(data);

  // needs to be `any` type, since we not decide type due to too much value uncertainty, also `Autocomplete` won't allow options with varying type
  const options: any = React.useMemo(() => {
    if (!latestData) {
      return [];
    }
    const { projects, reviews } = latestData;
    const options = [
      ...projects.items.map((project: Project) => ({ ...project, type: 'Projects' })),
      ...reviews.items.map((review: Review) => ({ ...review, type: 'Reviews' }))
    ];
    return options.length > 0 ? options : [EMPTY_OPTION];
  }, [latestData]);

  // only enable the component after it is mounted
  React.useEffect(() => setLoading(false), []);

  const showSpinner = loading || isValidating;

  const handleSelectReview = React.useCallback(async (lang, url) => {
    setLoading(true);
    await verifyCaptcha();
    sendEvent('magicbar-review-select');
    return redirectToUrl({
      url: createReviewUrl(url, lang)
    });
  }, [verifyCaptcha]);

  const handleSelectProject = React.useCallback(async (lang, url, subFolder) => {
    setLoading(true);
    await verifyCaptcha();
    sendEvent('magicbar-project-select');
    return redirectToUrl({
      url: createProjectUrl(`${url}${subFolder ? `/${subFolder}` : ''}`, lang)
    });
  }, [verifyCaptcha]);

  const handleCreateReview = React.useCallback(async (lang, url, isLiteUser, maxReviews) => {
    try {
      setLoading(true);
      await verifyCaptcha();
      if (!isValidReviewUrl(url)) {
        throw Object.assign(new Error('Invalid url'), { errorCode: 'INVALID_URL', url });
      } else if (!isValidReviewUrl(url, { homepageOnly: true })) {
        throw Object.assign(new Error('Not a homepage'), { errorCode: 'NO_HOMEPAGE_URL', url });
      }

      const reviewsData = isLiteUser ? await getReviewCount() : null;
      const reviewsLeft = isLiteUser ? maxReviews - reviewsData.reviews.count : null;
      if (isLiteUser && reviewsLeft !== null && reviewsLeft <= 0) {
        return redirectToUrl({
          url: `/${lang}/create/review`
        });
      }
      const { review, generation } = await createReview(url);
      sendEvent('magicbar-review-create');
      return redirectToUrl({
        url: `${createReviewUrl(review.url, lang)}?generationId=${encodeURIComponent(generation.id)}`
      });
    } catch (err: any) {
      if (err.errorCode === 'EXISTS') {
        return handleSelectReview(lang, url);
      } else if (err.errorCode === 'TRIAL_REVIEW_LIMIT') {
        setLoading(false);
        return setGenerationError(err);
      } else if (!err.errorCode) {
        console.error(err);
        Sentry.captureException(err);
      }
      sendEvent('magicbar-review-create-failed');
      setLoading(false);
      setGenerationError(err);
    }
  }, [handleSelectReview, verifyCaptcha]);

  const rootClassNames = classnames(
    classes.root,
    { [classes.focused]: focused },
    'nr-nav-magicbar'
  );

  return (
    <div className={rootClassNames}>
      <GenerationErrorDialog
        open={generationError && generationError.errorCode !== 'TRIAL_REVIEW_LIMIT'}
        onClose={() => setGenerationError(null)}
        error={generationError}
      />
      <TrialLimitDialog
        open={generationError && generationError.errorCode === 'TRIAL_REVIEW_LIMIT'}
        onClose={() => setGenerationError(null)}
      />
      <Autocomplete
        fullWidth
        filterOptions={(x) => x}
        disableClearable
        freeSolo
        autoComplete
        includeInputInList
        open={focused}
        getOptionLabel={(option) => {
          if (option === EMPTY_OPTION) {
            return input;
          }
          if (!option.url) {
            return option;
          }
          return `${option.url}${option.type === 'Projects' && option.subFolder ? `/${option.subFolder}/*` : ''}`;
        }}
        options={options}
        disabled={disabled}
        groupBy={option => option.type}
        onChange={(event, value, reason) => {
          skipNextBlur.current = true;
          if (value === EMPTY_OPTION) {
            return handleCreateReview(language, input, isLiteUser, maxReviews);
          } else if (reason === 'create-option') {
            return handleCreateReview(language, value, isLiteUser, maxReviews);
          } else if (reason === 'select-option') {
            if (typeof value !== 'string' && value?.url) {
              return (
                value?.type === 'Projects'
                  ? handleSelectProject(language, value.url, value.subFolder)
                  : handleSelectReview(language, value.url));
            }
          }
        }}
        ListboxProps={{
          style: {
            maxHeight: '80vh'
          }
        }}
        renderOption={(option: any) => (option === EMPTY_OPTION
          ? (
            <Translate content='magicbar_no_suggestion_cta' with={{ url: input }} />
            )
          : (
            <>
              <img
                className={classes.favicon}
                src={`https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(option.resolvedOrigin || option.url)}`}
              />
              {option.url}{option.type === 'Projects' && option.subFolder ? `/${option.subFolder}/*` : ''}
            </>
            ))}
        PopperComponent={WidePopper}
        inputValue={input}
        onInputChange={(event, newInputValue) => {
          if (loading) {
            // disable while loading. Can't use disabled prop as that takes foxus away
            return;
          }
          setInput(newInputValue);
        }}
        renderInput={(params) => {
          return (
            <InputBase
              inputProps={params.inputProps}
              {...params.InputProps}
              ref={params.InputProps.ref}
              placeholder={translate('website_improve')}
              className={classnames(
                classes.input,
                {
                  [classes.inputFocused]: focused
                },
                params.InputProps.className
              )}
              onFocus={() => {
                onFocus();
                sendEvent('magicbar-focus');
              }}
              onBlur={() => {
                if (skipNextBlur.current) {
                  skipNextBlur.current = false;
                  return;
                }
                onBlur();
              }}
              disabled={loading}
              endAdornment={
                showSpinner
                  ? <CircularProgress color='inherit' size={20} />
                  : <Icon type='search' size='md' />
              }
            />
          );
        }}
      />
    </div>
  );
}
